import { useGetExtractedFiles, useListSamples } from "@js2/hooks";
import { useMemo } from "react";
import {
  IExtractedFilesQueryParams,
  ISamplesQuery
} from "../../../hooks/@types";
import { ExtractedFilesSample } from "../../samples/@types";
import { SearchGridData } from "../../search/@types";
import useAuxiliaryExtractedFiles from "../../samples/components/rl_auxiliary_analysis/hooks/useAuxiliaryExtractedFiles";

const useSelectAllQueryHashes = (
  samplesQuery?: ISamplesQuery | undefined,
  extractedSampleQuery?: IExtractedFilesQueryParams | undefined
) => {
  const auxiliarySourceId = extractedSampleQuery?.auxiliary_source_id;
  const shouldFetchSamples = !!samplesQuery;
  const shouldFetchExtractedSamples = !!extractedSampleQuery;
  const shouldFetchAuxiliaryExtractedSamples =
    shouldFetchExtractedSamples && !!auxiliarySourceId;

  const { samples, isLoading: isLoadingSamplesList } =
    useListSamples(samplesQuery);

  const { extractedFileResults, isLoading: isLoadingExtractedFiles } =
    useGetExtractedFiles(
      shouldFetchAuxiliaryExtractedSamples
        ? undefined
        : extractedSampleQuery?.hash,
      extractedSampleQuery
    );

  const {
    results: auxiliaryExtractedFileResults,
    isLoading: isLoadingAuxiliaryExtractedFiles
  } = useAuxiliaryExtractedFiles(
    auxiliarySourceId ?? "",
    { ...extractedSampleQuery },
    { enabled: shouldFetchAuxiliaryExtractedSamples }
  );

  const querySamples: SearchGridData | ExtractedFilesSample[] | undefined =
    useMemo(() => {
      if (shouldFetchSamples) {
        return samples;
      } else if (shouldFetchAuxiliaryExtractedSamples) {
        return auxiliaryExtractedFileResults?.map(item => item.sample);
      } else if (shouldFetchExtractedSamples) {
        return extractedFileResults?.map(item => item.sample);
      } else {
        return undefined;
      }
    }, [
      isLoadingSamplesList,
      shouldFetchSamples,
      shouldFetchExtractedSamples,
      shouldFetchAuxiliaryExtractedSamples,
      isLoadingExtractedFiles,
      isLoadingAuxiliaryExtractedFiles
    ]);

  const shouldUseQueryHashes =
    shouldFetchSamples || shouldFetchExtractedSamples;

  const subscribeQueryHashes = shouldFetchSamples
    ? (querySamples as SearchGridData)?.map(sample => ({
        sha1: sample.sha1,
        available: sample.sample_available,
        location: sample.location
      }))
    : (querySamples as ExtractedFilesSample[])?.map(sample => ({
        sha1: sample.sha1,
        available: true,
        location: "local"
      }));

  const unsubscribeQueryHashes = querySamples?.map(sample => sample.sha1);

  return {
    subscribeQueryHashes,
    unsubscribeQueryHashes,
    shouldUseQueryHashes,
    isLoadingQueryHashes:
      isLoadingSamplesList || isLoadingAuxiliaryExtractedFiles
  };
};

export default useSelectAllQueryHashes;
